import React from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { message } from 'antd';
import { MdClear, MdAttachFile } from 'react-icons/md';

const MAX_FILE_SIZE_MB = 300;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1000 * 1000;

const allowedFileTypes = {
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/csv': ['.csv'],
  'text/plain': ['.txt'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/pdf': ['.pdf'],
  'application/xml': ['.xml'],
  'text/xml': ['.xml'],
  'application/x-rar-compressed': ['.rar'],
  'application/zip': ['.zip', '.zipx'],
  'application/x-7z-compressed': ['.7z']
};

const fileStore = new Map();

export const getStoredFile = (fileId) => fileStore.get(fileId);
export const getStoredFiles = (fields) => fields.map(f => fileStore.get(f.fileId)).filter(Boolean);
export const clearFileStore = () => fileStore.clear();

const FileUpload = ({ name = "fileDrop", children }) => {
  const { control, formState: { errors, submitCount }, clearErrors } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name });

  const onDrop = (acceptedFiles) => {
    clearErrors(name);
    acceptedFiles.forEach(file => {
      // Check for duplicates
      const isDuplicate = fields.some(f => f.fileName === file.name && f.fileSize === file.size);
      if (isDuplicate) {
        message.warning(`Filen "${file.name}" har redan lagts till`);
        return;
      }

      const fileId = `${file.name}-${file.size}-${Date.now()}`;
      // Store the actual File object in the map
      fileStore.set(fileId, file);
      // Append serializable metadata to react-hook-form
      append({
        fileId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
      });
    });
  };

  const onDropRejected = (fileRejections) => {
    if (fileRejections.some(({ file }) => file.size > MAX_FILE_SIZE)) {
      message.error('Filstorleken är för stor');
    } else {
      message.error('Ogiltig filtyp för filöverföringen');
    }
  };

  const handleRemove = (index) => {
    const field = fields[index];
    if (field?.fileId) {
      fileStore.delete(field.fileId);
    }
    remove(index);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: allowedFileTypes,
    maxSize: MAX_FILE_SIZE,
    onDropRejected
  });

  const hasError = submitCount > 0 && errors[name];

  return (
    <>
      <section>
        <div className='dropzone card__body' {...getRootProps()}>
          <input {...getInputProps()} />
          {children}
        </div>
      </section>
      {hasError && (
        <span className="help-text help-text--has-error">
          {errors[name]?.message}
        </span>
      )}
      {fields.map((field, index) => (
        <div className="card mt1" key={field.id}>
          <div className="card-header">
            <MdAttachFile className="card-header__icon" />
            <span className="card-header__title">{field.fileName}</span>
            <div className="card-header__actions">
              <button
                className="card__action card__action--remove"
                type="button"
                title="Remove file"
                onClick={() => handleRemove(index)}
              >
                <MdClear />
              </button>
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export default FileUpload;